import { customElement, FASTElement, observable } from '@microsoft/fast-element';
import { IProjectService, IVppaService } from '../../../../services';
import { IProjectDocumentStatus, IVppa, WatchlistProject } from '../../../../types';
import { PRODUCT_TYPE, PROFILE_RIGHTS, PROJECT_OPERATIONAL_STATUS } from '../../../../utils';
import { IndividualProjectsDrilldownStyles as styles } from './individual-projects-drilldown.styles';
import { IndividualProjectsDrilldownTemplate as template } from './individual-projects-drilldown.template';
import {
  starHighlightedIcon,
  starUnHighlightedIcon,
  getHtmlIconByName,
  getTechnologyIconName,
} from '../../../../styles';
import {
  formatDateLong,
  getFillColorFromProjectStatus,
  getFormattedNumber,
} from '../../../../utils/formatting';
import { Message, SocketSubscription } from '@genesislcap/foundation-comms';
import { updateArray } from '../../../../utils/dataserver';
import { IUserService } from '../../../../services/user.service';

interface SellerTileValueItem {
  value: any;
  icon: any;
}
interface SellerTileValue {
  title: string;
  items: Array<SellerTileValueItem>;
}

interface IRowObject {
  title: string;
  value: string;
}

interface IProductObject {
  name: string;
  type: boolean;
}

export enum ProductType {
  VPPA = 'Virtual PPA',
  PPA = 'Physical PPA',
  REC = 'RECs Only',
}

const name = 'individual-projects-drilldown';

@customElement({
  name,
  template,
  styles,
})
export class IndividualProjectsDrilldown extends FASTElement {
  private isClicked = false;
  public favProjectWatchlist: HTMLButtonElement;
  @IProjectService projectService: IProjectService;
  @observable project: IProjectDocumentStatus;
  @observable tileList: Array<SellerTileValue> = [];
  @observable vppaSubscription: SocketSubscription;
  @IVppaService vppaService: IVppaService;
  @observable vppaList: Array<any> = [];
  @observable generalInformation: Array<IRowObject> = [];
  @observable productType: Array<IProductObject> = [];
  @IUserService userService: IUserService;
  @observable projectWatchlistSubscription: SocketSubscription;
  @observable watchlist: Array<WatchlistProject> = [];
  @observable showWatchlist: boolean = false;
  @observable selectedVppa: IVppa;
  @observable selectedProductType: PRODUCT_TYPE;
  connectedCallback() {
    super.connectedCallback();
    this.getWatchlistButtonStatus();
  }

  private getWatchlistButtonStatus() {
    if (!this.userService.getFilteredPermissions().includes(PROFILE_RIGHTS.BUYER_VISIBILITY)) {
      return;
    }

    this.showWatchlist = true;

    let resultList = [];

    if (this.project) {
      this.projectWatchlistSubscription = this.projectService
        .getAllWatchlistProjects(
          "WATCHLIST_ENTITY_ID == '" +
            this.project.PROJECT_ID +
            "' && WATCHLIST_ENTITY_ID_TYPE == 'PROJECT'",
        )
        .subscribe({
          next: (streamMessage: Message) => {
            resultList = streamMessage.ROW ?? null;
            if (resultList) {
              this.watchlist = updateArray(resultList, this.watchlist);
              if (this.watchlist.length > 0) {
                this.favProjectWatchlist.innerHTML = starHighlightedIcon();
                this.favProjectWatchlist.title = 'Remove from Watchlist';
                this.isClicked = true;
              } else {
                this.favProjectWatchlist.innerHTML = starUnHighlightedIcon();
                this.favProjectWatchlist.title = 'Add to Watchlist';
                this.isClicked = false;
              }
            }
          },
          error: (error: any) => {},
        });
    }
  }

  public favoriteWatchlistProject() {
    this.isClicked = !this.isClicked;

    if (this.isClicked) {
      this.projectService.eventInsertFavoritedProject(this.project.PROJECT_ID);
    } else {
      this.projectService.eventDeleteFavoritedProject(this.project.PROJECT_ID);
    }
  }

  setProject(project: IProjectDocumentStatus) {
    if (project) {
      this.project = project;
      this.getWatchlistButtonStatus();
      this.getListedContract();
    }
  }

  updateProject(project: IProjectDocumentStatus) {
    if (project) {
      this.generalInformation.splice(0);
      this.vppaList.splice(0);
      this.productType.splice(0);
      this.setProject(project);
    }
  }

  private getListedContract() {
    let resultList = [];
    this.vppaSubscription = this.vppaService
      .getAllVppas(
        "PROJECT_ID == '" +
          this.project.PROJECT_ID +
          "' && (VPPA_STATUS == 'ACTIVE' || VPPA_STATUS == 'EXISTING_INTEREST' || VPPA_STATUS == 'TRADE_PENDING')",
      )
      .subscribe({
        next: (streamMessage: Message) => {
          resultList = streamMessage.ROW ?? null;
          if (resultList) {
            this.vppaList = updateArray(resultList, this.vppaList);
          }
          if (this.generalInformation.length === 0) {
            this.loadGeneralInformation();
          }
          this.loadProductType();
        },
        error: (error: any) => {},
      });
  }

  private loadGeneralInformation() {
    this.generalInformation.push({
      title: 'Nameplate Capacity',
      value: getFormattedNumber(this.project.MEGAWATT_AC),
    });
    this.generalInformation.push({
      title: 'Offered Contracts',
      value: this.vppaList.length.toString(),
    });
    this.generalInformation.push({
      title: 'ISO',
      value: this.project.ISO,
    });
    if (this.project.PROJECT_OPERATIONAL_STATUS === PROJECT_OPERATIONAL_STATUS.NEW_BUILD) {
      this.generalInformation.push({
        title: 'Guaranteed COD',
        value: formatDateLong(this.project.GUARANTEED_COD),
      });
    } else {
      this.generalInformation.push({
        title: 'COD',
        value: formatDateLong(this.project.COD),
      });
    }
  }

  private loadProductType() {
    this.productType = [];
    this.productType.push({
      name: ProductType.VPPA,
      type: !!this.vppaList.find((vppa) => vppa.PRODUCT_TYPE == PRODUCT_TYPE.VIRTUAL_PPA),
    });
    this.productType.push({
      name: ProductType.PPA,
      type: !!this.vppaList.find((vppa) => vppa.PRODUCT_TYPE == PRODUCT_TYPE.PHYSICAL_PPA),
    });
    this.productType.push({
      name: ProductType.REC,
      type: !!this.vppaList.find((vppa) => vppa.PRODUCT_TYPE == PRODUCT_TYPE.RECS),
    });
  }

  public favoriteWatchlistVppa(data) {
    data.isClicked
      ? this.vppaService.eventInsertFavoritedVppa(data.VPPA_ID)
      : this.vppaService.eventDeleteFavoritedVppa(data.VPPA_ID);
  }

  public selectVppa(vppa: IVppa) {
    this.selectedVppa = vppa;
  }

  public getTechnologyIcon() {
    const name = getTechnologyIconName(this.project?.TECHNOLOGY);
    const fill = getFillColorFromProjectStatus(this.project?.PROJECT_OPERATIONAL_STATUS);

    return getHtmlIconByName({
      name: name,
      slot: 'start',
      width: '12px',
      height: '13px',
      fill: fill,
    });
  }
}
